import {useState} from "react";
import {useNavigate} from "react-router";
import {motion, useReducedMotion} from "motion/react";
import {Check, CircleAlert, Lock} from "lucide-react";
import {toast} from "sonner";
import {Input} from "../../ui";
import {logout as apiLogout, resetPassword} from "../../../services/authService";
import {tokenStorage} from "../../../services/api";
import {useAuth} from "../../../contexts/AuthContext";
import {hapticError} from "../../../utils/haptics";
import {PATHS} from "../../../routing";
import {RESET_ERRORS, RESET_PASSWORD_MAX_LENGTH, resetPasswordErrorText} from "./resetPassword";

/**
 * Тексты проверок формы вынесены в константы (сами строки не изменились).
 * Форма знает не только «что не так», но и «где не так», и подсвечивает кольцом
 * именно то поле, о котором говорит плашка ошибки — ровно как форма входа
 * (LoginForm: текст живёт один раз, плашкой сверху, поле несёт только признак).
 */
const FIELD_ERRORS = {
    token: "Введите токен из письма",
    password: "Пароль не менее 6 символов",
    confirm: "Пароли не совпадают",
} as const;

interface ResetTokenFormProps {
    /** Токен из ссылки письма (/reset-password?token=…) — уже извлечён из URL и передан модалке. */
    token: string;
}

/**
 * Новый пароль по ссылке из письма. Раньше это была отдельная страница
 * /reset-password (ResetPasswordPage); теперь это режим модалки поверх лендинга,
 * ровно как окно входа — логика формы перенесена без изменений.
 */
export function ResetTokenForm({token: tokenFromUrl}: ResetTokenFormProps) {
    const navigate = useNavigate();
    const {setUser} = useAuth();

    const [token, setToken] = useState(tokenFromUrl);
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const reduceMotion = useReducedMotion();

    /** Пружина экрана — эталон диалога подтверждения: без отскока, 0.32 c. */
    const spring = reduceMotion
        ? {duration: 0.15}
        : ({type: "spring", bounce: 0, duration: 0.32} as const);

    /** Какое поле подсветить. Ошибка сервера ни к одному полю не относится — только плашка. */
    const errorField =
        error === FIELD_ERRORS.token
            ? "token"
            : error === FIELD_ERRORS.password || error === RESET_ERRORS.password
                ? "password"
                : error === FIELD_ERRORS.confirm
                    ? "confirm"
                    : null;

    /**
     * Локальный отказ формы: текст ошибки + тактильный отклик.
     *
     * Эта форма сообщает об ошибках валидации только текстом, без `toast.error`,
     * поэтому общий перехват из `utils/toastHaptics.ts` её не покрывает. Ошибку
     * же сервера ниже (`toast.error`) он покрывает — там повторный вызов дал бы
     * двойную вибрацию, и его специально нет.
     */
    const rejectWith = (message: string) => {
        setError(message);
        hapticError();
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        const t = token.trim();
        if (t.length < 10) {
            rejectWith(FIELD_ERRORS.token);
            return;
        }
        if (password.length < 6) {
            rejectWith(FIELD_ERRORS.password);
            return;
        }
        if (password !== confirm) {
            rejectWith(FIELD_ERRORS.confirm);
            return;
        }
        setSubmitting(true);
        const res = await resetPassword(t, password);
        setSubmitting(false);
        if (res.error) {
            // Всплывающего уведомления здесь нет: тот же текст уже стоит
            // плашкой над формой, а красная плашка и красный тост одновременно —
            // это два сигнала об одном событии (apple-design, «utility»).
            // 422 сервера приходит сырым JSON pydantic — показываем русский текст (#1342).
            setError(resetPasswordErrorText(res.error, res.code));
            return;
        }
        // Ссылку открыли с живой сессией (#1342): после сброса она всё равно мертва
        // (сервер отклоняет токены, выданные до смены пароля), а «Войти» ведёт на
        // гостевой вход. Выходим сразу, пока фоновый запрос не поймал 401 и не
        // перезагрузил страницу поверх экрана успеха.
        if (tokenStorage.getAccessToken() || tokenStorage.getRefreshToken()) {
            await apiLogout();
            setUser(null);
        }
        setDone(true);
        toast.success("Пароль обновлён. Войдите с новым паролем.");
    };

    // Успех: галочка в той же точке экрана, где была форма, и «Войти» уводит
    // на обычный вход — модалка сама переключится на логин (/?auth=login).
    if (done) {
        return (
            <motion.div
                initial={reduceMotion ? {opacity: 0} : {opacity: 0, scale: 0.96}}
                animate={{opacity: 1, scale: 1}}
                transition={spring}
                className="py-6 text-center"
            >
                <div className="mb-4 flex justify-center">
                    <div className="flex size-16 items-center justify-center rounded-full bg-green-100">
                        <Check className="size-10 text-green-600" strokeWidth={2.4}/>
                    </div>
                </div>
                <h3 className="mb-2 text-xl font-bold text-gray-800">Пароль обновлён</h3>
                <p className="mb-6 text-sm font-medium text-gray-600">Войдите с новым паролем</p>
                <button
                    type="button"
                    className="rla-btn rla-btn-cta rla-btn-full"
                    onClick={() => navigate(PATHS.landingAuth("login"), {replace: true})}
                >
                    Войти
                </button>
            </motion.div>
        );
    }

    return (
        <form onSubmit={(e) => void onSubmit(e)} noValidate className="space-y-4">
            {/* Ошибка — та же плашка, что на входе (.rla-formalert): alarm-палитра,
                иконка, текст ровно один раз. Не голая красная строчка. */}
            {error && (
                <motion.div
                    key={error}
                    role="alert"
                    initial={reduceMotion ? {opacity: 0} : {opacity: 0, y: -6}}
                    animate={{opacity: 1, y: 0}}
                    transition={spring}
                    className="rla-formalert"
                >
                    <CircleAlert className="rla-formalert-icon size-5" aria-hidden="true"/>
                    <div className="min-w-0">
                        <p className="rla-formalert-title">{error}</p>
                    </div>
                </motion.div>
            )}

            {/* Токен обычно уже в адресе письма — поле появляется только если ссылка пришла без него. */}
            {!tokenFromUrl && (
                <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700" htmlFor="reset-token">
                        Токен
                    </label>
                    <div className="relative">
                        <Lock className="absolute left-4 top-1/2 size-5 -translate-y-1/2 text-gray-400"/>
                        <Input
                            id="reset-token"
                            size="lg"
                            value={token}
                            onChange={(e) => {
                                setToken(e.target.value);
                                setError(null);
                            }}
                            placeholder="Из ссылки в письме"
                            autoComplete="off"
                            error={errorField === "token"}
                            className="pl-10"
                        />
                    </div>
                </div>
            )}

            <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700" htmlFor="reset-password">
                    Новый пароль
                </label>
                <div className="relative">
                    <Lock className="absolute left-4 top-1/2 size-5 -translate-y-1/2 text-gray-400"/>
                    <Input
                        id="reset-password"
                        type="password"
                        size="lg"
                        value={password}
                        onChange={(e) => {
                            setPassword(e.target.value);
                            setError(null);
                        }}
                        autoComplete="new-password"
                        maxLength={RESET_PASSWORD_MAX_LENGTH}
                        error={errorField === "password"}
                        className="pl-10"
                    />
                </div>
            </div>

            <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700" htmlFor="reset-confirm">
                    Повторите пароль
                </label>
                <div className="relative">
                    <Lock className="absolute left-4 top-1/2 size-5 -translate-y-1/2 text-gray-400"/>
                    <Input
                        id="reset-confirm"
                        type="password"
                        size="lg"
                        value={confirm}
                        onChange={(e) => {
                            setConfirm(e.target.value);
                            setError(null);
                        }}
                        autoComplete="new-password"
                        maxLength={RESET_PASSWORD_MAX_LENGTH}
                        error={errorField === "confirm"}
                        className="pl-10"
                    />
                </div>
            </div>

            <button type="submit" disabled={submitting} className="rla-btn rla-btn-cta rla-btn-full">
                {submitting ? "Сохранение…" : "Сохранить пароль"}
            </button>
        </form>
    );
}